import { useEffect, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { CheckCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

export function ConsentSuccessPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { tenant_name?: string }
  const [countdown, setCountdown] = useState(10)

  const tenantName = search.tenant_name || 'the tenant'

  useEffect(() => {
    // Auto-redirect after 10 seconds
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          navigate({ to: '/admin/tenants' })
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [navigate])

  const handleGoToTenants = () => {
    navigate({ to: '/admin/tenants' })
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-50 dark:from-gray-900 dark:to-gray-800 p-4">
      <Card className="max-w-2xl w-full shadow-xl">
        <CardHeader className="text-center pb-6">
          <div className="flex justify-center mb-4">
            <div className="rounded-full bg-green-100 dark:bg-green-900 p-6">
              <CheckCircle className="h-16 w-16 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <CardTitle className="text-3xl font-bold text-green-900 dark:text-green-100">
            {t('admin.consent.successTitle', 'Admin Consent Granted!')}
          </CardTitle>
          <CardDescription className="text-lg mt-2">
            {t(
              'admin.consent.successDescription',
              'You have successfully granted admin consent for {{tenantName}}',
              { tenantName }
            )}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <h3 className="font-semibold text-green-900 dark:text-green-100 mb-2">
              {t('admin.consent.whatHappensNext', 'What happens next?')}
            </h3>
            <ul className="space-y-2 text-sm text-green-800 dark:text-green-200">
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>
                  {t(
                    'admin.consent.step1',
                    'Users from {{tenantName}} can now sign in to Crisis Buddy using their Azure AD accounts',
                    { tenantName }
                  )}
                </span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>
                  {t(
                    'admin.consent.step2',
                    'The tenant has been activated and is ready to use'
                  )}
                </span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>
                  {t(
                    'admin.consent.step3',
                    'You can manage tenant settings in the Admin panel'
                  )}
                </span>
              </li>
            </ul>
          </div>

          <div className="text-center text-sm text-muted-foreground">
            {t(
              'admin.consent.autoRedirect',
              'You will be automatically redirected to the Tenant Management page in {{seconds}} seconds...',
              { seconds: countdown }
            )}
          </div>

          <div className="flex gap-3">
            <Button onClick={handleGoToTenants} className="flex-1" size="lg">
              {t('admin.consent.goToTenantManagement', 'Go to Tenant Management')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
